/* eslint-disable react-refresh/only-export-components */
// https://gist.github.com/ilkou/7bf2dbd42a7faf70053b43034fc4b5a4?permalink_comment_id=5194437#gistcomment-5194437

import React, { ReactElement, ReactNode, Ref } from "react";
import SelectComponent, {
  components,
  ActionMeta,
  ClassNamesConfig,
  DropdownIndicatorProps,
  GroupBase,
  StylesConfig,
  MultiValueProps,
  MultiValueRemoveProps,
  ClearIndicatorProps,
  OptionProps,
  MenuProps,
  MenuListProps,
  OnChangeValue,
  Props,
  SelectInstance,
  ControlProps,
} from "react-select";
// For the keepLocked augmentation below, as react-select documents it
import type {} from "react-select/base";
import { cn, firstNotUndefined } from "@/lib/utils";
import { LuCheck, LuX } from "react-icons/lu";
import { GoChevronDown } from "react-icons/go";
import { useMenuPortal } from "./menu-portal-context";
import { useCopyPaste } from "./copy-paste";
import { combineRefs } from "@/hooks/combine-refs";
import { DropdownOnlyPropsCommon, useDropDownOnly } from "./drop-down-only";

/** select option type */
export type OptionType<Value = string | number> = {
  label: string;
  value: Value;
  icon?: ReactNode;
  className?: string;
  isLocked?: boolean;
  /**
   * A locked option this user may still remove: picked from the menu in this edit, or the user's
   * role may unassign locked tags. It keeps the locked look but not the protection.
   */
  isRemovable?: boolean;
  multiValue?: React.HTMLProps<HTMLDivElement> & { icon?: ReactNode };
};

/** A locked chip the user may not remove: its remove X is hidden and clear-all keeps it. */
export const isProtectedChip = (option: unknown) => {
  const chip = option as OptionType | undefined;
  return !!chip?.isLocked && !chip.isRemovable;
};

/** Marks a locked option as one this user may remove. */
export const markRemovable = <Option extends OptionType<unknown>>(
  option: Option,
): Option => (option?.isLocked ? { ...option, isRemovable: true } : option);

export type KeepLockedProps = {
  /**
   * Protect chips (see isProtectedChip): hide their remove X, and keep them through Backspace,
   * Delete and the clear-all X. For pickers that assign tags; filters leave it off, since a locked
   * tag picked as a filter value is still just a filter (ID-7150).
   */
  keepLocked?: boolean;
};

// keepLocked is passed on to react-select itself, so the remove X's class name and the clear-all
// X can read it from selectProps
declare module "react-select/base" {
  /* eslint-disable @typescript-eslint/no-unused-vars -- a merged declaration repeats react-select's
     own type parameters */
  export interface Props<
    Option,
    IsMulti extends boolean,
    Group extends GroupBase<Option>,
  > {
    /** See KeepLockedProps */
    keepLocked?: boolean;
  }
  /* eslint-enable @typescript-eslint/no-unused-vars */
}

/**
 * Wraps a select's onChange so protected chips survive the removals their hidden remove X can't
 * stop: Backspace (pop-value), Delete on a chip focused with ArrowLeft (remove-value), and
 * react-select's own clear-all X (clear). Saving a tag set without a locked tag is refused for
 * roles that can't unassign locked tags (ID-7095).
 */
export const keepLockedChips =
  <Option, IsMulti extends boolean>(
    onChange?: (
      value: OnChangeValue<Option, IsMulti>,
      action: ActionMeta<Option>,
    ) => void,
  ) =>
  (value: OnChangeValue<Option, IsMulti>, action: ActionMeta<Option>) => {
    if (
      (action.action === "remove-value" || action.action === "pop-value") &&
      isProtectedChip(action.removedValue)
    ) {
      return;
    }
    if (
      action.action === "clear" &&
      action.removedValues.some(isProtectedChip)
    ) {
      const removed = action.removedValues.filter(
        (option) => !isProtectedChip(option),
      );
      if (removed.length > 0) {
        onChange?.(
          action.removedValues.filter(isProtectedChip) as OnChangeValue<
            Option,
            IsMulti
          >,
          { ...action, removedValues: removed },
        );
      }
      return;
    }
    onChange?.(value, action);
  };

export const isGroup = <
  Value,
  Option extends OptionType<Value>,
  Group extends GroupBase<Option>,
>(
  optionOrGroup: Option | Group,
): optionOrGroup is Group => !!(optionOrGroup as Group)?.options;

export const defaultPlaceholder = "";

/**
 * styles that aligns with shadcn/ui
 */
const controlStyles = {
  base: "flex !flex-nowrap !min-h-0 w-full rounded-md bg-transparent pl-3 py-0 pr-1 gap-1 transition-colors hover:cursor-pointer",
  border: "border border-input",
  focus: "outline-none ring-1 ring-ring/20",
  disabled: "cursor-not-allowed opacity-50",
};
const placeholderStyles = "text-muted-foreground";
const valueContainerStyles =
  "gap-1 min-h-7 h-full max-h-20 !overflow-y-auto flex items-center flex-wrap";
export const multiValueStyles =
  "inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 px-1.5 py-0.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring/20 focus:ring-offset-2";
export const multiValueLabelStyles = "inline-flex items-center gap-1";
const indicatorsContainerStyles = "gap-1";
const clearIndicatorStyles = "p-1 rounded-md";
const indicatorSeparatorStyles = {
  base: "",
  border: "bg-border/20",
};
const dropdownIndicatorStyles = "p-0 rounded-md";
const menuStyles =
  "p-1 mt-1 border border-border/20 bg-popover shadow-md rounded-md text-popover-foreground";
const groupHeadingStyles = "py-2 px-1 text-secondary-foreground font-semibold";
const optionStyles = {
  base: "hover:cursor-pointer hover:bg-accent hover:text-accent-foreground px-2 py-1.5 rounded-sm !text-base !cursor-default !select-none !outline-none font-sans h-full",
  focus: "active:bg-accent/90 bg-accent text-accent-foreground",
  disabled: "pointer-events-none opacity-50",
  selected: "",
};
const noOptionsMessageStyles =
  "text-accent-foreground p-2 bg-accent border border-dashed border-border/20 rounded-sm";
const loadingIndicatorStyles =
  "flex items-center justify-center h-4 w-4 opacity-50";
const loadingMessageStyles = "text-accent-foreground p-2 bg-accent";

export type SelectInputClassNameOptions = {
  override?: { [k in keyof ClassNamesConfig]?: boolean } | boolean;
  border?: boolean;
};

export type SelectInputStylesOptions = {
  override?: { [k in keyof StylesConfig]?: boolean } | boolean;
};

/**
 * This factory method is used to build custom classNames configuration
 */
export const createClassNames = <
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  classNames?: ClassNamesConfig<Option, IsMulti, Group>,
  options?: SelectInputClassNameOptions,
): ClassNamesConfig<Option, IsMulti, Group> => {
  const border = options?.border ?? true;
  const override = options?.override as Exclude<
    NonNullable<typeof options>["override"],
    boolean
  >;
  return {
    ...(options?.override === true ? {} : classNames),
    clearIndicator:
      options?.override === true || override?.clearIndicator
        ? classNames?.clearIndicator
        : (state) =>
            cn(clearIndicatorStyles, classNames?.clearIndicator?.(state)),
    container: (state) => cn(classNames?.container?.(state)),
    control:
      options?.override === true || override?.control
        ? classNames?.control
        : (state) =>
            cn(
              controlStyles.base,
              border && controlStyles.border,
              state.isDisabled && controlStyles.disabled,
              state.isFocused && controlStyles.focus,
              classNames?.control?.(state),
            ),
    dropdownIndicator:
      options?.override === true || override?.dropdownIndicator
        ? classNames?.dropdownIndicator
        : (state) =>
            cn(dropdownIndicatorStyles, classNames?.dropdownIndicator?.(state)),
    group: (state) => cn(classNames?.group?.(state)),
    groupHeading:
      options?.override === true || override?.groupHeading
        ? classNames?.groupHeading
        : (state) => cn(groupHeadingStyles, classNames?.groupHeading?.(state)),
    indicatorsContainer:
      options?.override === true || override?.indicatorsContainer
        ? classNames?.indicatorsContainer
        : (state) =>
            cn(
              indicatorsContainerStyles,
              classNames?.indicatorsContainer?.(state),
            ),
    indicatorSeparator:
      options?.override === true || override?.indicatorSeparator
        ? classNames?.indicatorSeparator
        : (state) =>
            cn(
              indicatorSeparatorStyles.base,
              border && indicatorSeparatorStyles.border,
              classNames?.indicatorSeparator?.(state),
            ),
    input: (state) => cn(classNames?.input?.(state)),
    loadingIndicator:
      options?.override === true || override?.loadingIndicator
        ? classNames?.loadingIndicator
        : (state) =>
            cn(loadingIndicatorStyles, classNames?.loadingIndicator?.(state)),
    loadingMessage:
      options?.override === true || override?.loadingMessage
        ? classNames?.loadingMessage
        : (state) =>
            cn(loadingMessageStyles, classNames?.loadingMessage?.(state)),
    menu:
      options?.override === true || override?.menu
        ? classNames?.menu
        : (state) => cn(menuStyles, classNames?.menu?.(state)),
    menuList: (state) => cn(classNames?.menuList?.(state)),
    menuPortal: (state) => cn(classNames?.menuPortal?.(state)),
    multiValue:
      options?.override === true || override?.multiValue
        ? classNames?.multiValue
        : (state) =>
            cn(
              multiValueStyles,
              (state.data as OptionType)?.isLocked &&
                "opacity-80 border-border border",
              (state.data as OptionType)?.className,
              classNames?.multiValue?.(state),
            ),
    multiValueLabel:
      options?.override === true || override?.multiValueLabel
        ? classNames?.multiValueLabel
        : (state) =>
            cn(multiValueLabelStyles, classNames?.multiValueLabel?.(state)),
    multiValueRemove: (state) =>
      cn(
        state.selectProps.keepLocked &&
          isProtectedChip(state.data) &&
          "!hidden",
        classNames?.multiValueRemove?.(state),
      ),
    noOptionsMessage:
      options?.override === true || override?.noOptionsMessage
        ? classNames?.noOptionsMessage
        : (state) =>
            cn(noOptionsMessageStyles, classNames?.noOptionsMessage?.(state)),
    option:
      options?.override === true || override?.option
        ? classNames?.option
        : (state) =>
            cn(
              optionStyles.base,
              state.isFocused && optionStyles.focus,
              state.isDisabled && optionStyles.disabled,
              state.isSelected && optionStyles.selected,
              classNames?.option?.(state),
            ),
    placeholder:
      options?.override === true || override?.placeholder
        ? classNames?.placeholder
        : (state) => cn(placeholderStyles, classNames?.placeholder?.(state)),
    singleValue: (state) => cn(classNames?.singleValue?.(state)),
    valueContainer:
      options?.override === true || override?.valueContainer
        ? classNames?.valueContainer
        : (state) =>
            cn(valueContainerStyles, classNames?.valueContainer?.(state)),
    ...(options?.override === true ? classNames : {}),
  };
};

export const createStyles = <
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  styles?: StylesConfig<Option, IsMulti, Group>,
  options?: SelectInputStylesOptions,
): StylesConfig<Option, IsMulti, Group> => {
  const override = options?.override as Exclude<
    NonNullable<typeof options>["override"],
    boolean
  >;
  return {
    ...(options?.override === true ? {} : styles),
    input:
      options?.override === true || override?.input
        ? styles?.input
        : (base, prop) => ({
            ...base,
            "input:focus": {
              boxShadow: "none",
            },
            ...styles?.input?.(base, prop),
          }),
    multiValueLabel:
      options?.override === true || override?.multiValueLabel
        ? styles?.multiValueLabel
        : (base, prop) => ({
            ...base,
            whiteSpace: "normal",
            overflow: "visible",
            ...styles?.multiValueLabel?.(base, prop),
          }),
    multiValueRemove:
      options?.override === true || override?.multiValueRemove
        ? styles?.multiValueRemove
        : (base, prop) => ({
            ...base,
            ...(prop.selectProps.keepLocked && isProtectedChip(prop.data)
              ? { display: undefined }
              : {}),
            ...styles?.multiValueRemove?.(base, prop),
          }),
    control:
      options?.override === true || override?.control
        ? styles?.control
        : (base, prop) => ({
            ...base,
            // transition: "none",
            minHeight: undefined,
            // minHeight: '2.25rem', // we used !min-h-9 instead
            ...styles?.control?.(base, prop),
          }),
    menuPortal:
      options?.override === true || override?.menuPortal
        ? styles?.menuPortal
        : (base, prop) => ({
            ...base,
            zIndex: 9999,
            ...styles?.menuPortal?.(base, prop),
          }),
    menu:
      options?.override === true || override?.menu
        ? styles?.menu
        : (base, prop) => ({
            ...base,
            zIndex: 9999,
            // zIndex: "900000 !important",
            // "::-webkit-transform": "translate3d(0, 0, 0)",
            // transform: "translate3d(0, 0, 0)",
            ...styles?.menu?.(base, prop),
          }),
    menuList:
      options?.override === true || override?.menuList
        ? styles?.menuList
        : (base, prop) => ({
            ...base,
            "::-webkit-scrollbar": {
              background: "transparent",
            },
            "::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "::-webkit-scrollbar-thumb": {
              background: "hsl(var(--border))",
            },
            "::-webkit-scrollbar-thumb:hover": {
              background: "transparent",
            },
            ...styles?.menuList?.(base, prop),
          }),
  };
};

export const useStyles = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>({
  styles,
  options,
}: {
  styles?: StylesConfig<Option, IsMulti, Group>;
  options?: SelectInputStylesOptions;
}) => {
  return React.useMemo(
    () => createStyles(styles, options),
    [styles, options?.override],
  );
};

export const useClassNames = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>({
  defaultClassNames,
  classNames,
  options,
}: {
  defaultClassNames?: ClassNamesConfig<Option, IsMulti, Group>;
  classNames?: ClassNamesConfig<Option, IsMulti, Group>;
  options?: SelectInputClassNameOptions;
}) => {
  return React.useMemo(
    () =>
      createClassNames<Option, IsMulti, Group>(
        { ...defaultClassNames, ...classNames },
        options,
      ),
    [classNames, options?.override, options?.border],
  );
};

/**
 * React select custom components
 */
export const Control = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  props: ControlProps<Option, IsMulti, Group>,
) => {
  return (
    <components.Control {...props}>
      {props?.getValue()?.[0]?.icon}
      {props.children}
    </components.Control>
  );
};

const icon =
  "rounded-full text-muted-foreground hover:text-foreground hover:bg-ghost";

export const DropdownIndicator = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  props: DropdownIndicatorProps<Option, IsMulti, Group>,
) => {
  return (
    <components.DropdownIndicator {...props}>
      <GoChevronDown className={cn("h-4 w-4 mr-1", icon)} />
    </components.DropdownIndicator>
  );
};

export const ClearIndicator = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  props: ClearIndicatorProps<Option, IsMulti, Group>,
) => {
  return (
    <components.ClearIndicator
      {...props}
      innerProps={
        // A select that keeps locked chips clears only the others; any other clears everything
        props.isMulti && props.selectProps.keepLocked
          ? {
              ...props.innerProps,
              onMouseDown: () => {
                props.setValue(
                  props.getValue().filter(isProtectedChip) as any,
                  "deselect-option",
                  undefined,
                );
              },
            }
          : props.innerProps
      }
    >
      <LuX className={cn("h-3.5 w-3.5", icon)} />
    </components.ClearIndicator>
  );
};

export const MultiValue = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  props: MultiValueProps<Option, IsMulti, Group>,
) => {
  const { icon, ...multiValueProps } = props?.data?.multiValue ?? {};
  return (
    <components.MultiValue
      {...props}
      innerProps={{ ...props.innerProps, ...multiValueProps }}
    >
      {icon}
      {props.children}
    </components.MultiValue>
  );
};

export const MultiValueLabel = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  props: MultiValueProps<Option, IsMulti, Group>,
) => {
  return <components.MultiValueLabel {...props} />;
};

export const MultiValueRemove = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  props: MultiValueRemoveProps<Option, IsMulti, Group>,
) => {
  return (
    <components.MultiValueRemove {...props}>
      <LuX className={cn("h-3 w-3", icon)} />
    </components.MultiValueRemove>
  );
};

export const Option = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  props: OptionProps<Option, IsMulti, Group>,
) => {
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2 justify-between h-full">
        <div
          className={cn(
            "flex items-center justify-between gap-2",
            props.data?.className,
          )}
        >
          {props?.data?.icon}
          {props.label}
        </div>
        {props.isSelected && <LuCheck className="h-full" />}
      </div>
    </components.Option>
  );
};

// Using Menu and MenuList fixes the scrolling behavior
export const Menu = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  props: MenuProps<Option, IsMulti, Group>,
) => {
  return <components.Menu {...props}>{props.children}</components.Menu>;
};

export const MenuList = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  props: MenuListProps<Option, IsMulti, Group>,
) => {
  return <components.MenuList {...props} />;
};

export type SelectInputProps<
  Value = string | number,
  Option extends OptionType<Value> = OptionType<Value>,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Props<Option, IsMulti, Group> &
  DropdownOnlyPropsCommon & {
    classNamesOptions?: SelectInputClassNameOptions;
    stylesOptions?: SelectInputStylesOptions;
  } & { copyPaste?: boolean } & KeepLockedProps;

const BaseSelect = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  props: SelectInputProps<Value, Option, IsMulti, Group>,
  ref: React.LegacyRef<SelectInstance<Option, IsMulti, Group>>,
) => {
  const _menuPortalTarget = useMenuPortal();
  const {
    styles: _styles,
    classNames: _classNames,
    classNamesOptions,
    stylesOptions,
    components,
    menuPortalTarget,
    placeholder = defaultPlaceholder,
    closeMenuOnSelect = !props.isMulti,
    onlyDropdownIndicator,
    keepLocked,
    ...rest
  } = props;
  const instanceId = React.useId();

  const styles = useStyles({
    styles: _styles,
    options: stylesOptions,
  });

  const classNames = useClassNames({
    defaultClassNames: React.useMemo(
      () =>
        ({
          valueContainer: (_s) => cn("min-w-[100px]"),
          control: (_s) => cn("h-9"),
        }) as ClassNamesConfig<Option, IsMulti, Group>,
      [],
    ),
    classNames: _classNames,
    options: classNamesOptions,
  });

  const {
    ref: copyPasteRef,
    copy: _copy,
    storeOptions: _storeOptions,
    ...copyPaste
  } = useCopyPaste<Value, Option, IsMulti, Group>({
    ...props,
  });

  const dropdown = useDropDownOnly<Value, Option, IsMulti, Group>({
    ...props,
    Control: (components?.Control ?? Control) as any,
  });

  return (
    <SelectComponent
      {...copyPaste}
      {...dropdown}
      ref={combineRefs(ref, copyPasteRef)}
      instanceId={instanceId}
      unstyled
      components={{
        // Control,
        DropdownIndicator,
        ClearIndicator,
        MultiValue,
        MultiValueRemove,
        Option,
        Menu,
        MenuList,
        ...copyPaste.components,
        ...components,
        ...dropdown.components,
      }}
      styles={styles}
      classNames={classNames}
      menuPortalTarget={firstNotUndefined(
        menuPortalTarget,
        _menuPortalTarget,
        document.body,
      )}
      placeholder={placeholder}
      closeMenuOnSelect={closeMenuOnSelect}
      {...rest}
      keepLocked={keepLocked}
      onChange={keepLocked ? keepLockedChips(rest.onChange) : rest.onChange}
    />
  );
};

export const SelectInput = React.forwardRef(BaseSelect) as <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  p: SelectInputProps<Value, Option, IsMulti, Group> & {
    ref?: Ref<React.LegacyRef<SelectInstance<Option, IsMulti, Group>>>;
  },
) => ReactElement;

export default SelectInput;
